import {
  FetchAssetTypesQuery,
  FetchImageCreatorsQuery,
  FetchImageModifiersQuery,
  FetchImageSizeQuery,
} from 'api/queries/creativeLibrary';
import { CREATIVE_LIBRARY_FILTER_OPTIONS } from 'modules/creativeLibrary/constants';
import { CreativeLibraryFilterPrefix, FilterOption, Filters } from 'modules/creativeLibrary/types';
import styled from 'styled-components';
import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import { Menu } from 'ui/menu';
import { ALIGNMENT } from 'ui/menu/constants';
import { Caption, colors, spacing } from 'ui/theme';
import { isString } from 'utils/helpers';
import { useGraphql } from 'utils/hooks/useGraphql';
import noop from 'utils/noop';

const StyledMenu = styled(Menu)`
  [data-test="menu-list-container"] {
    min-width: ${spacing.x12};
  }
`;

const StyledImageSizeContent = styled.div`
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  width: 150px;
`;

const StyledCaption = styled(Caption)`
  color: ${colors.textSecondary};
`;

export const content = {
  title: 'Filters',
};

const MAX_NO_FILTER_OPTIONS = 5;

const SIZE_OPTIONS = {
  Large: '> 10MB',
  Medium: '1MB - 10MB',
  Small: '< 1MB',
};

type Props = {
  onSelect?: (values: string[][]) => void;
  selectedFilters?: Filters;
};

export const CreativeLibraryFilterMenu = ({
  onSelect = noop,
  selectedFilters = [],
  ...props
}: Props) => {
  // queries
  // file type
  const { data: assetTypesData = {} } = useGraphql(FetchAssetTypesQuery);
  // image size
  const { data: imageSizeData = {} } = useGraphql(FetchImageSizeQuery);
  // creators
  const { data: assetCreatorsData = {} } = useGraphql(FetchImageCreatorsQuery);
  // modifiers
  const { data: assetModifiersData = {} } = useGraphql(FetchImageModifiersQuery);

  // format query data to readable arrays
  const assetTypesOptions = assetTypesData?.imageFileTypes?.map((type: string) => ({
    id: type,
    label: type,
  }));

  const imageSizeOptions = imageSizeData?.imageSizes?.map((size: string) => ({
    id: size,
    content: (
      <StyledImageSizeContent>
        {size} <StyledCaption>{SIZE_OPTIONS[size as keyof typeof SIZE_OPTIONS]}</StyledCaption>
      </StyledImageSizeContent>
    ),
  }));

  const queriedOptions: { [key: string]: any } = {
    [CreativeLibraryFilterPrefix.FileType]: assetTypesOptions,
    // when implementing more files than images, check with BE for the correct query
    [CreativeLibraryFilterPrefix.FileSize]: imageSizeOptions,
    [CreativeLibraryFilterPrefix.Creator]: assetCreatorsData?.imageCreators,
    [CreativeLibraryFilterPrefix.ModifiedBy]: assetModifiersData?.imageModifiers,
  };

  // Prefix options to prevent duplication
  const builtFilterOptions = CREATIVE_LIBRARY_FILTER_OPTIONS.map(
    ({ id, label, prefix, queryId, queryLabel }) => ({
      id,
      label,
      isSelectMenu: true,
      prefix,
      options:
        queriedOptions[prefix]?.map((secondaryOption: string | Record<string, string>) => {
          const isSecondaryOptionAString = isString(secondaryOption);

          if (isSecondaryOptionAString) {
            return {
              label: secondaryOption,
              id: `${prefix}${secondaryOption}`,
            };
          }

          const label = secondaryOption[queryLabel as string];
          const id = secondaryOption[queryId as string];
          const content = secondaryOption['content'];
          const options = secondaryOption['options'];
          const isExpandableGroup = secondaryOption['isExpandableGroup'];
          const childPrefix = secondaryOption['prefix'];

          return {
            // Assigns value from query to label
            // this label and id will vary from query to query, set in constants
            // for locales it would be something like instead of queryLabel languageName, and id would be tag

            label,
            id: `${prefix}${id}`,
            content,
            options,
            isExpandableGroup,
            prefix: childPrefix,
          };
        }) ?? [],
    }),
  );

  // Build Id Maps, an object of all ids by filter category and children
  const filterOptionIdsMap = builtFilterOptions.reduce(
    (
      acc: { [key: string]: string[] },
      {
        prefix,
        options,
      }: { prefix: string; options: { id: string; prefix: string; options?: { id: string }[] }[] },
    ) => {
      const childOptions = options.filter(({ options }) => options?.length);

      const updatedMap = childOptions.reduce(
        (childAcc, { prefix: childPrefix, options: childOptions }) => {
          const childIds = childOptions?.map(({ id }) => id) || [];

          return {
            ...childAcc,
            [childPrefix]: [...(childAcc[childPrefix] || []), ...childIds],
          };
        },
        { [prefix]: [...(acc[prefix] || []), ...options.map(({ id }) => id)] },
      );

      return {
        ...acc,
        ...updatedMap,
      };
    },
    {},
  );

  const parentChildPrefixMap = builtFilterOptions.reduce(
    (
      acc: Record<string, string[]>,
      {
        prefix,
        options,
      }: { prefix: string; options: { id: string; prefix: string; options?: { id: string }[] }[] },
    ) => {
      const childPrefixes = options
        .filter(
          ({ options, prefix: childPrefix }) =>
            options?.length && childPrefix && childPrefix !== prefix,
        )
        .map(({ prefix: childPrefix }) => childPrefix);

      if (childPrefixes.length > 0) {
        acc[prefix] = [...(acc[prefix] || []), ...childPrefixes];
      }
      return acc;
    },
    {},
  );

  const childrenFilterOptionIdsMap: { [key: string]: string[] } = builtFilterOptions.reduce(
    (acc, { options }: { options: { id: string; options?: { id: string }[] }[] }) => {
      const childOptionsMap = options.reduce((childAcc, { id, options: childOptions }) => {
        if (childOptions?.length) {
          return { ...childAcc, [id]: childOptions.map(({ id }) => id) };
        }
        return childAcc;
      }, {});
      return { ...acc, ...childOptionsMap };
    },
    {},
  );

  // Use Maps to calculate totals by filter category and transform into an object with totals by key
  const filterTotals: { [key: string]: number } = Object.keys(filterOptionIdsMap).reduce(
    (acc, key) => ({
      ...acc,
      [key]: filterOptionIdsMap[key].reduce(
        (total: number, filterId: string) =>
          selectedFilters.includes(filterId) ? total + 1 : total,
        0,
      ),
    }),
    {},
  );

  // Add totals to get full total
  const allFiltersTotal = Object.values(filterTotals).reduce(
    (total, filterTotal) => total + filterTotal,
    0,
  );

  // Add totals to options
  const builtFilterOptionsWithTotals = builtFilterOptions
    .map((option) => {
      const childTotal =
        parentChildPrefixMap[option.prefix]?.reduce(
          (acc, childPrefix) => acc + (filterTotals[childPrefix] || 0),
          0,
        ) || 0;

      return {
        ...option,
        hasFilter: option.options.length > MAX_NO_FILTER_OPTIONS,
        hasCountBadge: (filterTotals[option.prefix] || 0) + childTotal > 0,
      };
    })
    .filter(
      (filter) =>
        filter.options.length > 0 &&
        !Object.keys(parentChildPrefixMap).some((parentPrefix) =>
          parentChildPrefixMap[parentPrefix]?.includes(filter.prefix),
        ),
    );

  const handleSelection = (selectedId: string) => {
    const selectedIdIndex = selectedFilters.indexOf(selectedId);
    const childrenOptions = childrenFilterOptionIdsMap[selectedId] ?? [];
    let newSelectedFilters: FilterOption[];

    if (selectedIdIndex !== -1) {
      // Remove the selectedId and its children or the parent if selected
      newSelectedFilters = selectedFilters.filter(
        (filter) =>
          filter !== selectedId &&
          !childrenOptions.includes(filter as string) &&
          !Object.keys(childrenFilterOptionIdsMap).some(
            (parentId) =>
              parentId === filter && childrenFilterOptionIdsMap[parentId].includes(selectedId),
          ),
      );
    } else {
      // Add the selectedId and its children
      newSelectedFilters = [
        ...selectedFilters,
        selectedId,
        ...childrenOptions.filter((childOption) => !selectedFilters.some((o) => childOption === o)),
      ];

      // Check if all children of a parent are selected, and add the parent if necessary
      Object.keys(childrenFilterOptionIdsMap).forEach((parentId) => {
        const childOptions = childrenFilterOptionIdsMap[parentId];
        if (
          !newSelectedFilters.some((o) => o === parentId) &&
          childOptions.every((option) => newSelectedFilters.includes(option))
        ) {
          newSelectedFilters = [...newSelectedFilters, parentId];
        }
      });
    }

    // returns an 2d array mirroring the order of the filters, while filtering out stale filters
    const selected = CREATIVE_LIBRARY_FILTER_OPTIONS.map((o) =>
      newSelectedFilters.filter((selection) =>
        filterOptionIdsMap[o.prefix].includes(selection as string),
      ),
    ) as string[][];

    onSelect(selected);
  };

  return (
    <StyledMenu
      options={builtFilterOptionsWithTotals}
      defaultSelectedOptionIds={selectedFilters as string[]}
      handleSelectionChange={handleSelection}
      horizontalAlignment={ALIGNMENT.LEFT}
      {...props}
    >
      <FilterMenuTriggerButton selectedFilterCount={allFiltersTotal} />
    </StyledMenu>
  );
};
